import { MutationKey, useQueryClient } from '@tanstack/react-query';

import { CommonSuccess, GenericFailure, OffsetLimit } from '@playq/services-shared';
import { ClusterizationRunId, ClusterizationRunsResponse } from '@playq/services-beetle';
import { Environment } from '@playq/octopus-common';

import { services2 } from '/api/services2';
import { useEitherMutation, UseMutationOptionsExtended } from '/api/service-hooks';
import { snackbarService } from '/common/snackbarService';

import { queryRunsKeys } from './useQueryRuns';

export type DeleteRunsPayloadType = {
  ids: ClusterizationRunId[];
  env: Environment;
};

export const delteRunsKey: MutationKey = ['runs', 'mutation', 'delete'];

export const useDeleteRuns = (
  iterator: OffsetLimit,
  nextKeys: unknown[] | undefined,
  options?: UseMutationOptionsExtended<CommonSuccess, GenericFailure | Error, DeleteRunsPayloadType>
) => {
  const queryClient = useQueryClient();

  return useEitherMutation(({ ids, env }) => services2.audienceRequestsClusterizationService.deleteRuns(ids, env), {
    onSuccess: (_, variables) => {
      const deletedCount = variables.ids.length;

      queryClient.setQueriesData<ClusterizationRunsResponse>({ queryKey: queryRunsKeys }, (oldData) => {
        if (!oldData) return oldData;

        const newData = new ClusterizationRunsResponse();

        Object.assign(newData, oldData);

        newData.total = newData.total = Math.max(0, (oldData.total || 0) - deletedCount);

        return newData;
      });

      queryClient.invalidateQueries({
        queryKey: queryRunsKeys,
        refetchType: 'active',
        predicate: ({ queryKey }) => {
          const offsetLimit = queryKey.find((key) => key instanceof OffsetLimit);
          const queryOffset = offsetLimit?.offset;
          return queryOffset !== undefined && queryOffset >= iterator.offset;
        },
      });
      if (nextKeys) {
        queryClient.removeQueries({ queryKey: nextKeys });
      }

      snackbarService.success(`${variables.ids.length > 1 ? 'Runs have' : 'Run has'} been deleted`);
    },
    mutationKey: delteRunsKey,
    meta: {
      onErrorMessage: (err: GenericFailure) => ({
        message: `Failed to delete runs: ${err.message}`,
        log: true,
      }),
    },
    ...options,
  });
};
